import { entropyToMnemonic, mnemonicToEntropy } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { concatBytes, fromBase64, toBase64, toBase64Url, utf8 } from './bytes';
import {
  ACCOUNT_KDF_DEFAULT,
  ACCOUNT_KDF_RANGE,
  ACCOUNT_KDF_SALT_BYTES,
  ACCOUNT_KEY_BYTES,
  AccountHkdfInfo,
  ARGON2_PARAMS,
  IV_BYTES,
  WRAP_AAD_PREFIX,
  WRAP_VERSION,
} from './constants';
import { KdfParamsRejectedError, UnwrapFailedError, UnwrapFailure } from './errors';
import type { Argon2Params } from './keys';
import { requireValidPassphrase } from './passphrase';

/** Argon2id parameters of an account's login password, as stored on and sent by the server. */
export interface AccountKdfParams {
  /** Account salt: base64url without padding, 16 bytes. */
  readonly salt: string;
  /** Memory in KiB. */
  readonly m: number;
  readonly t: number;
  readonly p: number;
}

/** What a device derives from the login password. */
export interface LoginKeys {
  /** Sent to the server to log in: base64url without padding, 32 bytes. */
  readonly loginProof: string;
  /** AES-256-GCM key that wraps the recovery code (non-extractable); never leaves the device. */
  readonly wrapKey: CryptoKey;
}

export interface DeriveLoginKeysOptions {
  /**
   * Argon2id cost override. For tests only: it replaces the cost after the server's parameters passed
   * `assertAcceptableKdf`, and yields keys no real device can reproduce.
   */
  readonly argon2Override?: Argon2Params;
}

const EMPTY_SALT = new Uint8Array(0);
const ENTROPY_BYTES = 16;
const AUTH_TAG_BYTES = 16;
const WRAPPED_BYTES = 1 + IV_BYTES + ENTROPY_BYTES + AUTH_TAG_BYTES;

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) return null;
  const standard = text.replaceAll('-', '+').replaceAll('_', '/');
  return fromBase64(standard + '='.repeat((4 - (standard.length % 4)) % 4));
}

function inRange(value: number, min: number, max: number): boolean {
  return Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Refuse login KDF parameters outside the accepted range. A server that lowers them would make the login
 * password cheap to crack offline; one that raises them could stall the device.
 */
export function assertAcceptableKdf(params: AccountKdfParams): void {
  const salt = fromBase64Url(params.salt);
  // Canonical form only: a salt that does not round-trip is not what this client would have generated.
  if (salt === null || salt.length !== ACCOUNT_KDF_SALT_BYTES || toBase64Url(salt) !== params.salt) {
    throw new KdfParamsRejectedError();
  }
  const r = ACCOUNT_KDF_RANGE;
  if (!inRange(params.m, r.mMin, r.mMax) || !inRange(params.t, r.tMin, r.tMax) || !inRange(params.p, r.pMin, r.pMax)) {
    throw new KdfParamsRejectedError();
  }
}

/** Default cost with a fresh random salt, for registration, password change and reset. */
export function newAccountKdf(
  random: (length: number) => Uint8Array = (length) => globalThis.crypto.getRandomValues(new Uint8Array(length)),
): AccountKdfParams {
  return { salt: toBase64Url(random(ACCOUNT_KDF_SALT_BYTES)), ...ACCOUNT_KDF_DEFAULT };
}

/**
 * The Argon2id step of the login password: the 32-byte login root key. Runs in the worker in browsers.
 * The password is normalised with Unicode NFKC so that visually identical input from different keyboards or
 * platforms derives the same key; case and whitespace are kept, because passwords are case-sensitive.
 * `params` must already have passed `assertAcceptableKdf` (or be a test override).
 */
export async function computeLoginRootKey(
  password: string,
  salt: Uint8Array,
  params: Argon2Params,
): Promise<Uint8Array> {
  // Loaded on first use, like the passphrase step in keys.ts.
  const { argon2id } = await import('./argon2');
  return argon2id({
    password: password.normalize('NFKC'),
    salt,
    memorySize: params.memoryKiB,
    iterations: params.iterations,
    parallelism: params.parallelism,
    hashLength: ARGON2_PARAMS.hashLength,
    outputType: 'binary',
  });
}

/** The Argon2 cost that `params` stands for. */
export function kdfToArgon2(params: AccountKdfParams): Argon2Params {
  return { memoryKiB: params.m, iterations: params.t, parallelism: params.p };
}

/** The decoded account salt of range-checked parameters. */
export function kdfSaltBytes(params: AccountKdfParams): Uint8Array {
  assertAcceptableKdf(params);
  return fromBase64Url(params.salt) as Uint8Array;
}

async function hkdfKey(secret: Uint8Array): Promise<CryptoKey> {
  return globalThis.crypto.subtle.importKey('raw', secret as Uint8Array<ArrayBuffer>, 'HKDF', false, [
    'deriveBits',
    'deriveKey',
  ]);
}

const hkdfParams = (info: string): HkdfParams => ({
  name: 'HKDF',
  hash: 'SHA-256',
  salt: EMPTY_SALT,
  info: utf8(info),
});

/** The HKDF step: split the login root key into login proof and wrap key. Clears nothing; callers own `root`. */
export async function deriveLoginKeysFromRoot(root: Uint8Array): Promise<LoginKeys> {
  const subtle = globalThis.crypto.subtle;
  const hkdf = await hkdfKey(root);
  const [proofBits, wrapKey] = await Promise.all([
    subtle.deriveBits(hkdfParams(AccountHkdfInfo.LoginProof), hkdf, ACCOUNT_KEY_BYTES * 8),
    subtle.deriveKey(hkdfParams(AccountHkdfInfo.Wrap), hkdf, { name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]),
  ]);
  return { loginProof: toBase64Url(new Uint8Array(proofBits)), wrapKey };
}

/**
 * Derive the login proof and the wrap key on the calling thread. Prefer `deriveLoginKeysInWorker` in the
 * browser (Argon2 blocks for ~1 s). Throws `KdfParamsRejectedError` before any work for unsafe parameters.
 */
export async function deriveLoginKeys(
  password: string,
  params: AccountKdfParams,
  options: DeriveLoginKeysOptions = {},
): Promise<LoginKeys> {
  const salt = kdfSaltBytes(params);
  const root = await computeLoginRootKey(password, salt, options.argon2Override ?? kdfToArgon2(params));
  try {
    return await deriveLoginKeysFromRoot(root);
  } finally {
    root.fill(0);
  }
}

/** The recovery proof: base64url of HKDF(master key); proves possession of the recovery code to reset the password. */
export async function deriveRecoveryProof(master: Uint8Array): Promise<string> {
  const hkdf = await hkdfKey(master);
  const bits = await globalThis.crypto.subtle.deriveBits(
    hkdfParams(AccountHkdfInfo.RecoveryProof),
    hkdf,
    ACCOUNT_KEY_BYTES * 8,
  );
  return toBase64Url(new Uint8Array(bits));
}

/** Additional authenticated data: binds the wrapped code to its space. */
function wrapAad(spaceId: string): Uint8Array<ArrayBuffer> {
  return utf8(`${WRAP_AAD_PREFIX}|${spaceId}`);
}

/** Wrap a recovery code (12-word passphrase): base64 of 0x01 || IV(12) || AES-GCM(16-byte entropy). */
export async function wrapRecoveryCode(
  passphrase: string,
  wrapKey: CryptoKey,
  spaceId: string,
): Promise<string> {
  const entropy = mnemonicToEntropy(requireValidPassphrase(passphrase), wordlist);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  try {
    const sealed = await globalThis.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: wrapAad(spaceId) },
      wrapKey,
      entropy as Uint8Array<ArrayBuffer>,
    );
    return toBase64(concatBytes(new Uint8Array([WRAP_VERSION]), iv, new Uint8Array(sealed)));
  } finally {
    entropy.fill(0);
  }
}

/** Open a wrapped recovery code; returns the normalised 12-word passphrase or throws `UnwrapFailedError`. */
export async function unwrapRecoveryCode(
  wrapped: string,
  wrapKey: CryptoKey,
  spaceId: string,
): Promise<string> {
  const bytes = fromBase64(wrapped);
  if (bytes === null || bytes.length !== WRAPPED_BYTES) throw new UnwrapFailedError(UnwrapFailure.Malformed);
  if (bytes[0] !== WRAP_VERSION) throw new UnwrapFailedError(UnwrapFailure.UnsupportedVersion);
  const iv = bytes.subarray(1, 1 + IV_BYTES);
  let entropy: Uint8Array;
  try {
    entropy = new Uint8Array(
      await globalThis.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, additionalData: wrapAad(spaceId) },
        wrapKey,
        bytes.subarray(1 + IV_BYTES),
      ),
    );
  } catch {
    throw new UnwrapFailedError(UnwrapFailure.AuthenticationFailed);
  }
  try {
    return entropyToMnemonic(entropy, wordlist);
  } finally {
    entropy.fill(0);
  }
}
